import { Box, Slide, Typography } from "@mui/material";
import { Check, Clock } from "@procore/core-icons";
import { Button } from "@procore/core-react";
import { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    clearLastSceneBackfillingBackend,
    getLastSceneBackfillingBackend,
    isProcoreSceneReady,
    onSceneBackfilling,
    setLastSceneBackfillingBackend,
} from "apis/procore";
import type { SceneReadBackend } from "apis/procore/errors";
import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { IndicatorBar, IndicatorToast } from "components/offlineIndicator";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import type { ObjectDataAccess } from "dataAccess";
import { useRefreshAndRestoreState } from "hooks/useRefreshAndRestoreState";
import { explorerActions, selectProcoreContext, selectSceneDbBackfill, selectSceneId } from "slices/explorer";

import { useIsSceneBackfillBarVisible } from "./useIsSceneBackfillBarVisible";

/** How often to re-probe the scene while a backfill is in progress. */
const RECOVERY_POLL_INTERVAL_MS = 15_000;

type SceneBackfillDebugState = "init" | "preparing" | "ready";

declare global {
    interface Window {
        /**
         * DEV only: force the backfill UI into a state from the console. `"preparing"` also
         * starts the recovery poll against the current data access's backend, which flips to
         * `"ready"` once the scene answers.
         */
        setSceneBackfill?: (state: SceneBackfillDebugState) => void;
    }
}

const debugBackfillStates: Record<SceneBackfillDebugState, { preparing: boolean; ready: boolean }> = {
    init: { preparing: false, ready: false },
    preparing: { preparing: true, ready: false },
    ready: { preparing: false, ready: true },
};

/**
 * Tracks the BIM Search DB `202` (scene backfilling) state; renders nothing.
 *
 * The search-DB client emits a signal whenever any object/search/property call
 * returns 202 (scene database missing, backfill from FAS in progress). This enters
 * the "preparing" state and, when the scene ids are known, polls the backend that
 * reported the miss until the scene is ready. {@link SceneBackfillBar} shows it.
 */
export const SceneBackfillWatcher = memo(function SceneBackfillWatcher() {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const backfill = useAppSelector(selectSceneDbBackfill);
    const [backend, setBackend] = useState<SceneReadBackend>(() => getLastSceneBackfillingBackend() ?? "legacy");
    const procoreContext = useAppSelector(selectProcoreContext);
    const sceneId = useAppSelector(selectSceneId);
    const {
        state: { dataAccess },
    } = useExplorerGlobals();

    // Enter the "preparing" state whenever a search-DB call reports a 202, including one
    // that fired before mount — unless that backfill has already been seen through to ready.
    useEffect(() => {
        const unsubscribe = onSceneBackfilling((readBackend) => {
            setBackend(readBackend);
            dispatch(explorerActions.setSceneDbBackfill({ preparing: true, ready: false }));
        });

        const missedBackend = getLastSceneBackfillingBackend();
        const { preparing, ready } = selectSceneDbBackfill(store.getState());
        if (missedBackend && !preparing && !ready) {
            setBackend(missedBackend);
            dispatch(explorerActions.setSceneDbBackfill({ preparing: true, ready: false }));
        }

        return () => {
            unsubscribe();
            clearLastSceneBackfillingBackend();
        };
    }, [dispatch, store]);

    useEffect(() => {
        if (!import.meta.env.DEV) {
            return;
        }

        window.setSceneBackfill = (state) => {
            if (state === "init") {
                clearLastSceneBackfillingBackend();
            } else {
                const readBackend = readBackendFor(dataAccess);
                setLastSceneBackfillingBackend(readBackend);
                setBackend(readBackend);
            }
            dispatch(explorerActions.setSceneDbBackfill(debugBackfillStates[state]));
        };
        return () => {
            delete window.setSceneBackfill;
        };
    }, [dispatch, dataAccess]);

    // While preparing, poll until the backfill completes, then switch to the ready state.
    useEffect(() => {
        if (!backfill.preparing) {
            return;
        }

        const probe = resolveProbeIds(procoreContext, sceneId);
        if (!probe) {
            // Ids unavailable (e.g. non-Procore backend): keep the banner and rely on manual Refresh.
            return;
        }

        const controller = new AbortController();
        const timer = setInterval(async () => {
            try {
                const ready = await isProcoreSceneReady({ ...probe, backend, signal: controller.signal });
                if (ready && !controller.signal.aborted) {
                    // Don't reload on the user's behalf — surface a "ready, reload to view"
                    // banner and let them choose when to reload (agreed with design).
                    dispatch(explorerActions.setSceneDbBackfill({ preparing: false, ready: true }));
                }
            } catch {
                // Transient/other errors: keep polling until the scene is ready.
            }
        }, RECOVERY_POLL_INTERVAL_MS);

        return () => {
            controller.abort();
            clearInterval(timer);
        };
    }, [backfill.preparing, backend, procoreContext, sceneId, dispatch]);

    return null;
});

/**
 * Full-width "preparing" / "ready, reload to view" bar for the mobile HUD, styled and
 * placed like `OfflineConnectionBar`. Reloading stays user-initiated: the Refresh button
 * appears only once the DB is ready.
 */
export function SceneBackfillBar() {
    return (
        <IndicatorBar data-testid="scene-backfill-bar" role="status" aria-live="polite" sx={{ pointerEvents: "auto" }}>
            <SceneBackfillContent />
        </IndicatorBar>
    );
}

/** Desktop counterpart of {@link SceneBackfillBar}: the top-center chip `OfflineIndicator` uses. */
export function SceneBackfillToast() {
    const isVisible = useIsSceneBackfillBarVisible();
    // Slide needs a stable container ref to anchor the transform off of.
    const containerRef = useRef<HTMLDivElement | null>(null);

    return (
        <Box ref={containerRef} sx={{ overflow: "hidden", pointerEvents: "none" }}>
            <Slide
                in={isVisible}
                direction="down"
                container={containerRef.current}
                mountOnEnter
                unmountOnExit
                timeout={{ enter: 260, exit: 200 }}
            >
                <Box>
                    <IndicatorToast data-testid="scene-backfill-toast" role="status" aria-live="polite">
                        <SceneBackfillContent />
                    </IndicatorToast>
                </Box>
            </Slide>
        </Box>
    );
}

function SceneBackfillContent() {
    const { t } = useTranslation();
    const backfill = useAppSelector(selectSceneDbBackfill);
    const refresh = useRefreshAndRestoreState();

    return (
        <>
            {backfill.ready ? <Check size="md" /> : <Clock size="md" />}
            <Typography
                component="span"
                sx={{
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    lineHeight: "inherit",
                    letterSpacing: "inherit",
                    color: "inherit",
                    textAlign: "center",
                }}
            >
                {backfill.ready ? (
                    <Box component="span" sx={{ fontWeight: 600 }}>
                        {t("sceneDatabaseReady", "The scene database is ready. Reload to view it.")}
                    </Box>
                ) : (
                    <>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {t("sceneDatabasePreparing", "The scene database is being prepared.")}
                        </Box>{" "}
                        {t("sceneDatabasePreparingHint", "This can take a few minutes.")}
                    </>
                )}
            </Typography>
            {backfill.ready && (
                <Button variant="secondary" size="sm" onClick={refresh}>
                    {t("refresh")}
                </Button>
            )}
        </>
    );
}

/** Both BIM indexes address the BIM Query service; the object DB reads the legacy search DB. */
function readBackendFor(dataAccess: ObjectDataAccess | undefined): SceneReadBackend {
    return dataAccess && dataAccess.kind !== "object-db" ? "bim-query" : "legacy";
}

/**
 * The scene being backfilled is the one currently open, so its ids come from the
 * same state the search-DB calls use. `procoreContext.sceneVersionId` is optional
 * on input but is published back to the store once resolved during init
 * (see `useHandleInit`), so it is available here by the time a 202 can occur.
 */
function resolveProbeIds(
    procoreContext: { companyId: number; projectId: number; sceneVersionId?: string; sceneSliceId?: string } | null,
    sceneId: string | null,
):
    | { companyId: number; projectId: number; sceneId: string; sceneVersionId: string; sceneSliceId?: string }
    | undefined {
    if (procoreContext == null || !sceneId || !procoreContext.sceneVersionId) {
        return undefined;
    }

    return {
        companyId: procoreContext.companyId,
        projectId: procoreContext.projectId,
        sceneId,
        sceneVersionId: procoreContext.sceneVersionId,
        sceneSliceId: procoreContext.sceneSliceId,
    };
}
